import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MenuBranchSettings, MenuBranding, MenuSettings } from '@order-pilot/shared';
import { Alert } from '@/components/Alert';
import { AsyncSection } from '@/components/AsyncSection';
import { Button } from '@/components/Button';
import { CopyButton } from '@/components/CopyButton';
import { TextAreaField, TextField } from '@/components/Form';
import { PageHeader } from '@/components/PageHeader';
import { QrDownloadButton } from '@/components/QrDownloadButton';
import { SectionCard } from '@/components/SectionCard';
import { Skeleton } from '@/components/States';
import {
  fetchMenuSettings,
  portalKeys,
  updateMenuBranch,
  updateMenuBranding,
} from '@/features/portal/api';
import { getErrorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

const hideCat = (name: string) => `c:${name}`;
const hideItem = (name: string) => `p:${name}`;

/** Look of the public menu, for every branch. */
function BrandingCard({ branding }: { branding: MenuBranding }) {
  const queryClient = useQueryClient();
  // Persistent form state, reset only when the saved values change.
  const [draft, setDraft] = useState(branding);
  useEffect(() => setDraft(branding), [branding]);

  const save = useMutation({
    mutationFn: () => updateMenuBranding(draft),
    onSuccess: (next) => queryClient.setQueryData(portalKeys.menuSettings(), next),
  });
  const dirty = JSON.stringify(draft) !== JSON.stringify(branding);

  return (
    <SectionCard
      headingId="menu-branding"
      title="How the menu looks"
      description="Shared by every branch. Blank title uses your business name."
      action={
        <Button size="sm" variant="primary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      }
    >
      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
        <TextField
          id="menu-title"
          label="Title"
          maxLength={80}
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <div className="flex items-end gap-3">
          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700" htmlFor="menu-accent">
            Colour
            <input
              id="menu-accent"
              type="color"
              value={draft.accent}
              onChange={(e) => setDraft({ ...draft, accent: e.target.value.toUpperCase() })}
              className="h-10 w-16 cursor-pointer rounded border border-slate-300 bg-white"
            />
          </label>
          <label className="mb-2 flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.showPrices}
              onChange={(e) => setDraft({ ...draft, showPrices: e.target.checked })}
            />
            Show prices
          </label>
        </div>
        <div className="sm:col-span-2">
          <TextAreaField
            id="menu-welcome"
            label="Welcome note"
            hint="A line under the title, e.g. opening hours or Wi-Fi name."
            maxLength={300}
            rows={2}
            value={draft.welcome}
            onChange={(e) => setDraft({ ...draft, welcome: e.target.value })}
          />
        </div>
        {save.isError ? (
          <div className="sm:col-span-2">
            <Alert tone="error">{getErrorMessage(save.error)}</Alert>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}

/** One branch: on/off, its link, and what to hide. */
function BranchCard({ branch }: { branch: MenuBranchSettings }) {
  const queryClient = useQueryClient();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(branch.hidden));
  useEffect(() => setHidden(new Set(branch.hidden)), [branch.hidden]);

  const onSaved = (next: MenuSettings) => queryClient.setQueryData(portalKeys.menuSettings(), next);
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => updateMenuBranch(branch.id, { enabled }),
    onSuccess: onSaved,
  });
  const saveHidden = useMutation({
    mutationFn: () => updateMenuBranch(branch.id, { hidden: [...hidden] }),
    onSuccess: onSaved,
  });

  const dirty =
    hidden.size !== branch.hidden.length || branch.hidden.some((h) => !hidden.has(h));
  const flip = (key: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const error = toggle.isError ? toggle.error : saveHidden.isError ? saveHidden.error : null;

  return (
    <SectionCard
      headingId={`menu-branch-${branch.id}`}
      title={branch.name}
      description={
        branch.menuUpdatedAt
          ? `Menu last sent by the till ${formatDateTime(branch.menuUpdatedAt)}.`
          : branch.enabled
            ? 'Waiting for the till to send its menu — it does on its next sync.'
            : 'Switch on to publish this branch’s menu.'
      }
      action={
        <Button
          size="sm"
          variant={branch.enabled ? 'secondary' : 'primary'}
          loading={toggle.isPending}
          onClick={() => toggle.mutate(!branch.enabled)}
        >
          {branch.enabled ? 'Switch off' : 'Switch on'}
        </Button>
      }
    >
      <div className="space-y-4 px-5 py-4">
        {error ? <Alert tone="error">{getErrorMessage(error)}</Alert> : null}

        {branch.url ? (
          <div className="flex flex-wrap items-center gap-2">
            <code
              className={`min-w-0 flex-1 break-all rounded bg-slate-50 px-3 py-2 text-xs ${
                branch.enabled ? 'text-slate-700' : 'text-slate-400 line-through'
              }`}
            >
              {branch.url}
            </code>
            <CopyButton value={branch.url} size="sm" />
            {branch.enabled ? (
              <a
                className="btn btn-sm btn-ghost"
                href={branch.url}
                target="_blank"
                rel="noreferrer"
              >
                Open
              </a>
            ) : null}
          </div>
        ) : null}
        {branch.enabled && branch.url ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <QrDownloadButton
                url={branch.url}
                filename={`${branch.name}-menu-qr`}
                label="Download barcode"
              />
              <p className="text-xs text-slate-500">
                No table — for a counter, a takeaway bag, or a general link.
              </p>
            </div>

            {branch.tables.length > 0 ? (
              <div>
                <p className="mb-2 text-sm font-medium text-slate-900">Table QR codes</p>
                <div className="flex flex-wrap gap-2">
                  {branch.tables.map((table) => (
                    <QrDownloadButton
                      key={table}
                      url={`${branch.url}?t=${encodeURIComponent(table)}`}
                      filename={`${branch.name}-table-${table}-qr`}
                      label={`Table ${table}`}
                      variant="ghost"
                    />
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">
                No tables synced from the till yet — once it sends its table list, a code for each
                one appears here.
              </p>
            )}
          </div>
        ) : null}

        {branch.categories.length > 0 ? (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-medium text-slate-900">Show on the menu</p>
              <Button
                size="sm"
                variant="primary"
                disabled={!dirty}
                loading={saveHidden.isPending}
                onClick={() => saveHidden.mutate()}
              >
                Save
              </Button>
            </div>
            <p className="mb-3 text-xs text-slate-500">
              Untick to hide from customers. The till is not affected.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {branch.categories.map((cat) => {
                const catHidden = hidden.has(hideCat(cat.name));
                return (
                  <fieldset key={cat.name} className="rounded-lg border border-slate-200 p-3">
                    <legend className="px-1">
                      <label className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <input type="checkbox" checked={!catHidden} onChange={() => flip(hideCat(cat.name))} />
                        {cat.name}
                      </label>
                    </legend>
                    <ul className="mt-1 space-y-1">
                      {cat.items.map((item) => (
                        <li key={item}>
                          <label
                            className={`flex items-center gap-2 text-sm ${
                              catHidden ? 'text-slate-400' : 'text-slate-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              disabled={catHidden}
                              checked={!catHidden && !hidden.has(hideItem(item))}
                              onChange={() => flip(hideItem(item))}
                            />
                            {item}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}

/**
 * Smart menu settings — a view-only QR menu for customers. Every change here
 * is cloud-only: it never touches a till.
 */
export function SmartMenuPage() {
  const query = useQuery({
    queryKey: portalKeys.menuSettings(),
    queryFn: ({ signal }) => fetchMenuSettings(signal),
  });
  const settings = query.data;
  const branches = useMemo(() => settings?.branches ?? [], [settings]);

  return (
    <>
      <PageHeader
        title="Smart menu"
        description="A menu your customers open on their phone from a QR code on the table. Prices, photos and sold-out items come from the till."
      />
      <AsyncSection
        state={query}
        skeleton={
          <div className="card space-y-3 p-5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-72" />
          </div>
        }
      >
        {(data) =>
          !data.available ? (
            <Alert tone="info" title="Not part of your account">
              The smart menu is not switched on for your business. Contact support to add it.
            </Alert>
          ) : (
            <div className="space-y-6">
              <BrandingCard branding={data.branding} />
              {branches.map((branch) => (
                <BranchCard key={branch.id} branch={branch} />
              ))}
            </div>
          )
        }
      </AsyncSection>
    </>
  );
}
